import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  CardActions,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  Divider,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useSelector } from "react-redux";
import { ThemeProvider } from '@mui/material/styles';
import { getAppTheme } from '../../../services/authContext'; 
import { useDrawerContext } from '../../../components/DrawerContext/DrawerContext';
import { useConfirmation } from '../../../contexts/ConfirmationContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
// Import the actual API service
import { contactApi } from '../../../services/mle-api';

// --- Data and Helpers ---
const contactStatuses = [
    { code: 'NEW', label: 'New' },
    { code: 'CON', label: 'Contacted' },
    { code: 'RES', label: 'Resolved' },
    { code: 'SPM', label: 'Spam' }
];

const getStatusLabel = (statusCode) => {
    return contactStatuses.find(s => s.code === statusCode)?.label || statusCode;
};

const getStatusColor = (statusCode) => {
    switch(statusCode) {
        case 'NEW': return 'primary';
        case 'CON': return 'info';
        case 'RES': return 'success';
        case 'SPM': return 'error';
        default: return 'default';
    }
};

// --- CONTACT MANAGEMENT COMPONENT ---
const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  
  const { showDrawerForm } = useDrawerContext();
  const showConfirmation = useConfirmation();
  const { showSnackbar } = useSnackbar();
  const mode = useSelector((state) => state.theme.mode);
  const theme = getAppTheme(mode);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactApi.list();
      setContacts(response.data.results || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      showSnackbar('Failed to fetch contacts', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleStatusChange = async (id, newStatus) => {
    try {
        await contactApi.update(id, { status: newStatus });
        setContacts(prevContacts => 
            prevContacts.map(contact => 
                contact.id === id ? { ...contact, status: newStatus } : contact
            )
        );
        showSnackbar('Status updated successfully', { severity: 'success' });
    } catch (error) {
        showSnackbar('Failed to update status', { severity: 'error' });
    }
  };

  const handleDeleteContact = async (contact) => {
    const confirmed = await showConfirmation({
      title: 'Delete Contact Submission',
      message: `Are you sure you want to delete the submission from "${contact.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmButtonColor: 'error',
    });

    if (confirmed) {
      try {
        await contactApi.delete(contact.id);
        showSnackbar('Submission deleted successfully', { severity: 'success' });
        fetchContacts(); // Refresh list
      } catch (error) {
        showSnackbar('Failed to delete submission', { severity: 'error' });
      }
    }
  };

  const handleViewDetails = (contact) => {
    showDrawerForm({
        title: `Submission from ${contact.name}`,
        hideForm: true,
        content: (
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Contact Details</Typography>
                <Typography variant="body1"><strong>Name:</strong> {contact.name}</Typography>
                <Typography variant="body1"><strong>Email:</strong> {contact.email}</Typography>
                <Typography variant="body1"><strong>Phone:</strong> {contact.phone}</Typography>
                <Typography variant="body1"><strong>Date:</strong> {new Date(contact.submitted_at).toLocaleString()}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Message</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{contact.message}</Typography>
            </Box>
        )
    });
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(filter.toLowerCase()) ||
    contact.email.toLowerCase().includes(filter.toLowerCase())
  );

  const renderTableView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead sx={{ bgcolor: 'secondary.main' }}>
          <TableRow>
            <TableCell sx={{ color: 'white' }}>Contact</TableCell>
            <TableCell sx={{ color: 'white' }}>Message Snippet</TableCell>
            <TableCell sx={{ color: 'white' }}>Date</TableCell>
            <TableCell sx={{ color: 'white' }}>Status</TableCell>
            <TableCell sx={{ color: 'white' }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
          ) : filteredContacts.length > 0 ? (
            filteredContacts.map((contact) => (
              <TableRow key={contact.id} hover>
                <TableCell>
                    <Typography variant="body1" fontWeight="bold">{contact.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{contact.email}</Typography>
                </TableCell>
                <TableCell>{contact.message.substring(0, 50)}...</TableCell>
                <TableCell>{new Date(contact.submitted_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select value={contact.status} onChange={(e) => handleStatusChange(contact.id, e.target.value)}>
                        {contactStatuses.map(s => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton color="default" onClick={() => handleViewDetails(contact)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => handleDeleteContact(contact)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">No contact submissions found.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderGridView = () => (
    <Grid container spacing={3}>
        {loading ? (
            <Box sx={{width: '100%', display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress /></Box>
        ) : filteredContacts.length > 0 ? (
            filteredContacts.map(contact => (
                <Grid item xs={12} sm={6} md={4} key={contact.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>{contact.name.charAt(0)}</Avatar>
                                <Box>
                                    <Typography variant="h6">{contact.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">{new Date(contact.submitted_at).toLocaleDateString()}</Typography>
                                </Box>
                            </Box>
                            <Chip label={getStatusLabel(contact.status)} color={getStatusColor(contact.status)} size="small" sx={{ mb: 2 }}/>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}><EmailIcon fontSize="small" sx={{ mr: 1 }}/> {contact.email}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}><PhoneIcon fontSize="small" sx={{ mr: 1 }}/> {contact.phone}</Typography>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{contact.message.substring(0, 100)}..."</Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between' }}>
                            <FormControl size="small" sx={{ m: 1, minWidth: 120 }}>
                                <Select value={contact.status} onChange={(e) => handleStatusChange(contact.id, e.target.value)}>
                                    {contactStatuses.map(s => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Box>
                                <Tooltip title="View Details"><IconButton onClick={() => handleViewDetails(contact)}><VisibilityIcon /></IconButton></Tooltip>
                                <Tooltip title="Delete"><IconButton color="error" onClick={() => handleDeleteContact(contact)}><DeleteIcon /></IconButton></Tooltip>
                            </Box>
                        </CardActions>
                    </Card>
                </Grid>
            ))
        ) : (
            <Box sx={{width: '100%', textAlign: 'center', p: 4}}>
                <Typography>No contact submissions found.</Typography>
            </Box>
        )}
    </Grid>
  );

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Contact Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage all contact form submissions.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            placeholder="Search by name or email..."
            variant="outlined"
            size="small"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            sx={{ width: { xs: '100%', sm: '350px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewChange} size="small">
            <ToggleButton value="table" aria-label="table view"><TableRowsIcon /></ToggleButton>
            <ToggleButton value="grid" aria-label="grid view"><GridViewIcon /></ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {viewMode === 'table' ? renderTableView() : renderGridView()}

      </Container>
    </ThemeProvider>
  );
};


export default ContactManagement
